import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Banners = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageInputMode, setImageInputMode] = useState("upload"); // 'upload' | 'url'
  const fileInputRef = useRef(null);

  const getPreviewImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const trimmed = url.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
      return trimmed;
    }
    const cleanPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `https://ineedbuddy.com${cleanPath}`;
  };

  const [banner, setBanner] = useState({
    enabled: false,
    id: "banner_v1",
    title: "Special Welcome Offer! 🎉",
    subtitle: "Get flat 25% off on your first home service booking with verified buddies.",
    imageUrl: "",
    buttonText: "Explore Services →",
    actionUrl: "ServicesScreen",
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchBannerSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.status === "success" && json.data?.settings?.announcement) {
        setBanner((prev) => ({
          ...prev,
          ...json.data.settings.announcement,
        }));
      }
    } catch (err) {
      console.error("Error fetching banner settings:", err);
      showToast("Failed to load banner settings from server", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBannerSettings();
  }, [fetchBannerSettings]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please choose a valid image file (PNG, JPG, WebP)", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size must be less than 5MB", "error");
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(`${BASE_URL}admin/settings/upload-banner`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();
      if (json.status === "success" && json.data?.imageUrl) {
        setBanner((prev) => ({
          ...prev,
          imageUrl: json.data.imageUrl,
        }));
        showToast("Banner photo uploaded successfully! 🎉", "success");
      } else {
        showToast(json.message || "Failed to upload banner photo", "error");
      }
    } catch (err) {
      console.error("Error uploading banner image:", err);
      showToast("Network error uploading photo", "error");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSaveBanner = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        announcement: {
          ...banner,
          enabled: Boolean(banner.enabled),
          id: banner.id || `banner_${Date.now()}`,
        },
      };

      const res = await fetch(`${BASE_URL}admin/settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (json.status === "success") {
        showToast("Banner saved and published successfully! 🚀", "success");
      } else {
        showToast(json.message || "Failed to save banner", "error");
      }
    } catch (err) {
      console.error("Error saving banner:", err);
      showToast("Network error saving banner", "error");
    } finally {
      setSaving(false);
    }
  };

  const admin = JSON.parse(localStorage.getItem("admin") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  const handleGenerateNewId = () => {
    const newId = `banner_${Date.now()}`;
    setBanner((prev) => ({ ...prev, id: newId }));
    showToast(`New Banner ID generated: ${newId}. All users will see this as a fresh announcement.`, "info");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#1C1917] flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Container with lg:ml-72 to avoid sidebar overlap */}
      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Top Navbar */}
        <header className="bg-white/90 backdrop-blur-md border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-14 lg:pt-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-black text-stone-900 tracking-tight">
                    Banners & Announcements
                  </h1>
                  <p className="text-xs text-stone-500">
                    Control popup banners and marketing drawers for customer mobile app
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                  banner.enabled
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                    : "bg-stone-100 text-stone-600 border border-stone-300"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full mr-2 ${
                    banner.enabled ? "bg-emerald-500 animate-pulse" : "bg-stone-400"
                  }`}
                ></span>
                {banner.enabled ? "Active in Mobile App" : "Inactive / Hidden"}
              </span>

              <button
                onClick={handleSaveBanner}
                disabled={saving || loading}
                className="bg-stone-900 hover:bg-black text-white px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 shadow-xs disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-[#F8D346]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save & Publish</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Toast Alert */}
        {toast && (
          <div className="fixed top-20 right-8 z-50 animate-bounce">
            <div
              className={`px-4 py-3 rounded-xl shadow-lg text-xs font-bold flex items-center space-x-2 ${
                toast.type === "error"
                  ? "bg-rose-600 text-white"
                  : toast.type === "info"
                  ? "bg-blue-600 text-white"
                  : "bg-emerald-600 text-white"
              }`}
            >
              <span>{toast.message}</span>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Form Controls (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* Activation Card */}
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-xs">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-black text-stone-900 uppercase tracking-wider">
                      Banner Visibility Status
                    </h2>
                    <p className="text-xs text-stone-500 mt-1">
                      Toggle ON to display this banner in the customer app's bottom drawer
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={banner.enabled}
                      onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[4px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#FFCC0A]"></div>
                  </label>
                </div>
              </div>

              {/* Banner Details Form */}
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-xs space-y-5">
                <div className="flex items-center justify-between border-b border-stone-100 pb-4">
                  <h2 className="text-sm font-black text-stone-900 uppercase tracking-wider">
                    Banner Content & Settings
                  </h2>
                  <button
                    type="button"
                    onClick={handleGenerateNewId}
                    className="text-[11px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg border border-amber-200 transition-colors"
                  >
                    🔄 New Campaign ID
                  </button>
                </div>

                {/* Banner ID Tag */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Campaign Identifier (ID)
                  </label>
                  <input
                    type="text"
                    value={banner.id}
                    onChange={(e) => setBanner({ ...banner, id: e.target.value })}
                    className="w-full text-xs font-mono bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. banner_festive_offer_v1"
                  />
                  <p className="text-[11px] text-stone-400 mt-1">
                    Tip: Changing this ID causes the banner to reappear once for users who previously dismissed it.
                  </p>
                </div>

                {/* Banner Image Upload & Creative Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold text-stone-700">
                      Banner Image / Creative
                    </label>
                    <div className="flex items-center space-x-1 bg-stone-100 p-0.5 rounded-lg border border-stone-200">
                      <button
                        type="button"
                        onClick={() => setImageInputMode("upload")}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-all ${
                          imageInputMode === "upload"
                            ? "bg-white text-stone-900 shadow-xs"
                            : "text-stone-500 hover:text-stone-700"
                        }`}
                      >
                        📷 Upload Photo
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageInputMode("url")}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-all ${
                          imageInputMode === "url"
                            ? "bg-white text-stone-900 shadow-xs"
                            : "text-stone-500 hover:text-stone-700"
                        }`}
                      >
                        🔗 Image URL
                      </button>
                    </div>
                  </div>

                  {/* Hidden File Input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleFileUpload}
                  />

                  {imageInputMode === "upload" ? (
                    <div className="space-y-3">
                      {banner.imageUrl ? (
                        <div className="relative rounded-2xl border border-stone-200 overflow-hidden bg-stone-50 group">
                          <img
                            src={getPreviewImageUrl(banner.imageUrl)}
                            alt="Banner preview"
                            className="w-full h-44 object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22800%22%20height%3D%22500%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20800%20500%22%3E%3Crect%20fill%3D%22%23f3f4f6%22%20width%3D%22800%22%20height%3D%22500%22%2F%3E%3Ctext%20fill%3D%22%239ca3af%22%20font-family%3D%22sans-serif%22%20font-size%3D%2226%22%20dy%3D%2210%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3EBanner%20Image%20Preview%3C%2Ftext%3E%3C%2Fsvg%3E";
                            }}
                          />
                          <div className="absolute inset-0 bg-stone-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingImage}
                              className="px-3 py-1.5 bg-white text-stone-900 rounded-lg text-xs font-bold shadow-md hover:bg-stone-100 transition-all cursor-pointer"
                            >
                              🔄 Change Photo
                            </button>
                            <button
                              type="button"
                              onClick={() => setBanner({ ...banner, imageUrl: "" })}
                              disabled={uploadingImage}
                              className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold shadow-md hover:bg-rose-700 transition-all cursor-pointer"
                            >
                              🗑️ Remove
                            </button>
                          </div>
                          <div className="absolute top-2 left-2 px-2.5 py-1 rounded-md bg-stone-900/80 text-white text-[10px] font-bold backdrop-blur-xs flex items-center space-x-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                            <span>Active Photo</span>
                          </div>
                        </div>
                      ) : null}

                      {/* Dropzone / Upload button */}
                      <div
                        onClick={() => !uploadingImage && fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all ${
                          uploadingImage
                            ? "bg-stone-50 border-stone-300 opacity-60 cursor-not-allowed"
                            : "border-stone-300 hover:border-amber-400 hover:bg-amber-50/30 bg-white"
                        }`}
                      >
                        {uploadingImage ? (
                          <div className="flex flex-col items-center justify-center space-y-2 py-2">
                            <svg className="w-6 h-6 animate-spin text-amber-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                            </svg>
                            <p className="text-xs font-bold text-stone-700">Uploading banner image to server...</p>
                            <p className="text-[11px] text-stone-400">Please wait a moment</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center space-y-1.5 py-1">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-1">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <p className="text-xs font-bold text-stone-800">
                              {banner.imageUrl ? "Click to Replace Banner Photo" : "Click to Browse or Drag & Drop Banner Photo"}
                            </p>
                            <p className="text-[11px] text-stone-500">
                              Supports JPG, PNG, WebP (Max 5MB)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="text"
                        value={banner.imageUrl}
                        onChange={(e) => setBanner({ ...banner, imageUrl: e.target.value })}
                        className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                        placeholder="https://example.com/banner.png or /uploads/banners/..."
                      />
                    </div>
                  )}

                  {/* Measurement Box */}
                  <div className="mt-2.5 bg-amber-50/70 border border-amber-200/80 rounded-xl p-3 flex items-start space-x-2.5">
                    <span className="text-base">📐</span>
                    <div className="text-[11px] text-amber-900 leading-relaxed">
                      <span className="font-bold">Recommended Measurements: </span>
                      <strong>800 × 500 px</strong> (16:10 aspect ratio), top corner radius <strong>28px–32px</strong>. Keep important text 25px away from edges.
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Headline Title
                  </label>
                  <input
                    type="text"
                    value={banner.title}
                    onChange={(e) => setBanner({ ...banner, title: e.target.value })}
                    className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. Mega Summer Discount! ☀️"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">
                    Subtitle / Offer Description
                  </label>
                  <textarea
                    rows={2}
                    value={banner.subtitle}
                    onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })}
                    className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. Book any AC service or home cleaning and get flat ₹150 cashback."
                  />
                </div>

                {/* Button Text & Target */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-stone-700 mb-1">
                      Button Label
                    </label>
                    <input
                      type="text"
                      value={banner.buttonText}
                      onChange={(e) => setBanner({ ...banner, buttonText: e.target.value })}
                      className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder="e.g. Explore Services →"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 mb-1">
                      Action Target Screen
                    </label>
                    <select
                      value={banner.actionUrl}
                      onChange={(e) => setBanner({ ...banner, actionUrl: e.target.value })}
                      className="w-full text-xs bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <option value="ServicesScreen">Services Screen (Default)</option>
                      <option value="Home">Home Screen</option>
                      <option value="BookingsScreen">Bookings Screen</option>
                      <option value="ReferAndEarn">Refer & Earn Screen</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Live Mobile Mockup Preview (5 cols) */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-xs sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-black text-stone-900 uppercase tracking-wider flex items-center gap-2">
                    <span>📱</span>
                    <span>Live App Drawer Preview</span>
                  </h2>
                  <span className="text-[10px] uppercase font-bold text-stone-400 tracking-wider">
                    Customer View
                  </span>
                </div>

                {/* Realistic Phone Frame */}
                <div className="w-[310px] mx-auto bg-stone-900 rounded-[38px] p-3 shadow-2xl border-4 border-stone-800">
                  {/* Phone Screen */}
                  <div className="w-full bg-stone-100 rounded-[28px] overflow-hidden min-h-[460px] flex flex-col justify-end relative">
                    {/* Simulated Background */}
                    <div className="absolute inset-0 bg-stone-300/40 p-4 flex flex-col items-center justify-center opacity-40">
                      <div className="w-20 h-4 bg-stone-400 rounded-full mb-3"></div>
                      <div className="w-40 h-2 bg-stone-400 rounded-full mb-2"></div>
                      <div className="w-32 h-2 bg-stone-400 rounded-full"></div>
                    </div>

                    {/* Dark Backdrop Overlay */}
                    <div className="absolute inset-0 bg-stone-900/60 z-0"></div>

                    {/* Bottom Drawer Card */}
                    <div className="relative z-10 bg-white rounded-t-[28px] overflow-hidden shadow-xl border-t border-stone-200">
                      {/* Top Banner Image with 16:10 Ratio */}
                      <div className="w-full h-[180px] bg-stone-200 relative overflow-hidden">
                        {banner.imageUrl ? (
                          <img
                            src={getPreviewImageUrl(banner.imageUrl)}
                            alt="Banner Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22800%22%20height%3D%22500%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20800%20500%22%3E%3Crect%20fill%3D%22%23f3f4f6%22%20width%3D%22800%22%20height%3D%22500%22%2F%3E%3Ctext%20fill%3D%22%239ca3af%22%20font-family%3D%22sans-serif%22%20font-size%3D%2226%22%20dy%3D%2210%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3EBanner%20Image%20Preview%3C%2Ftext%3E%3C%2Fsvg%3E";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 text-xs font-semibold">
                            <span>Image URL Missing</span>
                            <span className="text-[10px] mt-1 text-stone-400">800 × 500 px</span>
                          </div>
                        )}

                        {/* Frosted Close Button */}
                        <div className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-stone-900/60 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white text-xs font-bold cursor-pointer">
                          ✕
                        </div>
                      </div>

                      {/* Content Area */}
                      <div className="p-4 bg-white">
                        <h3 className="font-extrabold text-stone-900 text-sm leading-snug">
                          {banner.title || "Headline Title Here"}
                        </h3>
                        <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">
                          {banner.subtitle || "Offer description and details will appear here."}
                        </p>

                        {/* Primary Button */}
                        <div className="mt-3.5 bg-[#FFCC0A] text-stone-950 font-bold text-xs py-2.5 px-4 rounded-xl text-center shadow-xs cursor-pointer hover:bg-amber-400 transition-colors">
                          {banner.buttonText || "Explore Services →"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-center text-[11px] text-stone-400 mt-4">
                  Matches customer app 1:1 aspect ratio and corner radius.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Banners;
